import { createFileRoute, notFound } from "@tanstack/react-router";
import { SiteHeader } from "@/components/SiteHeader";
import { SiteFooter } from "@/components/SiteFooter";
import { FaqSourceNote } from "@/components/FaqSourceNote";
import { pageHeadForProvince, getActiveSiteUrl } from "@/lib/seo-helpers";
import { buildBreadcrumbSchema, buildFaqSchema, buildSpeakableSchema } from "@/lib/schema";
import type { NaatiProvinceLanding } from "@/content/naati-corpus/provinces";
import { NAATI_FAQ_CATEGORIES } from "@/content/naati-corpus/faq-defs";
import { NAATI_PROVINCE_META } from "@/content/naati-corpus/meta.gen";

function localFaqs(p: NaatiProvinceLanding) {
  return [
    {
      q: `Where can I get a NAATI translation in ${p.provinceEn}?`,
      a: `No trip to Bangkok is required — ${p.logisticsEn} Every translation carries the stamp and CPN of a NAATI-certified translator, verifiable on the NAATI directory.`,
    },
    {
      q: `How is NAATI translation priced in ${p.provinceEn}?`,
      a: `Pricing is per page and by complexity: standard civil-registry documents have a flat per-document rate, while legal and financial documents are quoted by word count. Courier costs inside ${p.provinceEn} are charged at cost, and every job starts with a free written quote.`,
    },
    { q: `How long does it take from ${p.provinceEn}?`, a: p.turnaroundEn },
    { q: `Which documents do ${p.provinceEn} clients translate most?`, a: p.topDocsEn.join(" · ") },
    {
      q: `Can a NAATI translation from ${p.provinceEn} be uploaded straight to DHA?`,
      a: "Yes. The stamped and signed PDF uploads directly into ImmiAccount, and the same file is accepted by Immigration NZ, IRCC Canada and the UK Home Office.",
    },
    {
      q: "Do I have to send the original documents?",
      a: "High-resolution colour scans are enough for the translation itself, but keep the originals because a case officer can request them. Originals are required only when MFA legalisation runs alongside the translation.",
    },
  ];
}

export const Route = createFileRoute("/en/naati/province/$province")({
  loader: async ({ params }): Promise<{ p: NaatiProvinceLanding }> => {
    const { getNaatiProvinceLanding } = await import("@/content/naati-corpus/provinces");
    const p = getNaatiProvinceLanding(params.province);
    if (!p) throw notFound();
    return { p };
  },
  component: NaatiProvincePageEN,
  head: ({ params, loaderData }) => {
    if (!loaderData) {
      return { meta: [{ title: "Province not found" }, { name: "robots", content: "noindex" }] };
    }
    const site = getActiveSiteUrl();
    const { p } = loaderData;
    const title = `NAATI Translation ${p.provinceEn} 2026 — CPN Certified Translator`;
    const description = `NAATI-certified Thai ↔ English translation in ${p.provinceEn} for Australian and NZ visas — ${p.turnaroundEn}`;
    return {
      ...pageHeadForProvince({
        title,
        description,
        path: `/en/naati/province/${params.province}`,
        type: "article",
        locale: "en_US",
        alternates: [{ lang: "th", path: `/naati/province/${params.province}` }],
        keywords: [
          `naati translation ${p.provinceEn.toLowerCase()}`,
          `naati translator ${p.provinceEn.toLowerCase()}`,
          `thai to english certified translation ${p.provinceEn.toLowerCase()}`,
          `australian visa document translation ${p.provinceEn.toLowerCase()}`,
        ],
      }),
      scripts: [
        {
          type: "application/ld+json",
          children: JSON.stringify(
            buildBreadcrumbSchema([
              { name: "Home", url: `${site}/en` },
              { name: "NAATI Translation", url: `${site}/en/naati` },
              { name: `NAATI ${p.provinceEn}`, url: `${site}/en/naati/province/${params.province}` },
            ]),
          ),
        },
        { type: "application/ld+json", children: JSON.stringify(buildFaqSchema(localFaqs(p))) },
        { type: "application/ld+json", children: JSON.stringify(buildSpeakableSchema(`/en/naati/province/${params.province}`)) },
      ],
    };
  },
});

function NaatiProvincePageEN() {
  const { p } = Route.useLoaderData();
  const faqs = localFaqs(p);
  return (
    <>
      <SiteHeader />
      <main className="container mx-auto max-w-4xl px-4 py-10">
        <nav aria-label="breadcrumb" className="mb-4 text-sm text-muted-foreground">
          <a href="/en" className="hover:underline">Home</a> <span>/</span>{" "}
          <a href="/en/naati" className="hover:underline">NAATI Translation</a> <span>/</span>{" "}
          <span>{p.provinceEn}</span>
        </nav>

        <h1 className="mb-3 text-3xl font-bold md:text-4xl">
          NAATI Certified Translation in {p.provinceEn} — Australian & NZ Visas (2026)
        </h1>
        <p className="mb-6 text-lg text-muted-foreground">{p.demandEn}</p>

        <div className="mb-8 flex flex-wrap gap-2 text-sm">
          <a href="/contact" className="rounded-md bg-primary px-4 py-2 font-medium text-primary-foreground">Free quote →</a>
          <a href="/en/naati/faq" className="rounded-md border px-4 py-2 font-medium hover:bg-muted">NAATI FAQ</a>
          <a href={`/naati/province/${p.slug}`} className="rounded-md border px-4 py-2 text-muted-foreground hover:bg-muted">ภาษาไทย →</a>
        </div>

        <section className="mb-10">
          <h2 className="mb-3 text-xl font-bold">Most requested documents in {p.provinceEn}</h2>
          <ul className="list-disc space-y-1 pl-6 text-sm text-muted-foreground">
            {p.topDocsEn.map((d: string, i: number) => (
              <li key={i}>{d}</li>
            ))}
          </ul>
        </section>

        <section className="mb-10 rounded-lg border bg-card p-6">
          <h2 className="mb-2 text-xl font-bold">How document hand-off works from {p.provinceEn}</h2>
          <p className="mb-3 text-sm leading-relaxed text-muted-foreground">{p.logisticsEn}</p>
          <p className="text-sm leading-relaxed text-muted-foreground"><strong>Turnaround:</strong> {p.turnaroundEn}</p>
        </section>

        <section className="mb-10">
          <h2 className="mb-3 text-xl font-bold">FAQ — NAATI in {p.provinceEn}</h2>
          <div className="space-y-3">
            {faqs.map((f, i) => (
              <details key={i} className="rounded-lg border bg-card p-4" {...(i < 3 ? { open: true } : {})}>
                <summary className="cursor-pointer font-semibold text-sm">{f.q}</summary>
                <p className="faq-answer mt-2 text-sm leading-relaxed text-muted-foreground">{f.a}</p>
              </details>
            ))}
          </div>
        </section>

        <section className="mb-10">
          <h2 className="mb-3 text-xl font-bold">Browse NAATI FAQ by topic</h2>
          <div className="flex flex-wrap gap-2 text-xs">
            {NAATI_FAQ_CATEGORIES.map((c) => (
              <a key={c.slug} href={`/en/naati/faq/${c.slug}`} className="rounded-full border bg-muted/40 px-3 py-1 text-muted-foreground hover:bg-muted">
                {c.titleEn}
              </a>
            ))}
          </div>
        </section>

        <section className="mb-10">
          <h2 className="mb-3 text-xl font-bold">NAATI in other provinces</h2>
          <div className="flex flex-wrap gap-2 text-xs">
            {NAATI_PROVINCE_META.filter((x) => x.slug !== p.slug).map((x) => (
              <a key={x.slug} href={`/en/naati/province/${x.slug}`} className="rounded-full border bg-muted/40 px-3 py-1 text-muted-foreground hover:bg-muted">
                NAATI {x.provinceEn}
              </a>
            ))}
          </div>
        </section>

        <FaqSourceNote topic="translation" locale="en" />
      </main>
      <SiteFooter />
    </>
  );
}
